import { useState, useEffect, useMemo } from "react";
import { fetchExercises } from "../lib/fetchExercises.js";

/**
 * Fetch exercises and return a map id -> exercise for name/images lookup.
 * @param {string | null} organizationId
 * @param {boolean} enabled
 * @returns {{ exercises: Array, exercisesMap: object, loading: boolean, error: Error | null, getExerciseName: function, findById: function }}
 */
export function useExercisesMap(organizationId, enabled = true) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setExercises([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetchExercises(organizationId)
      .then(setExercises)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [organizationId, enabled]);

  const exercisesMap = useMemo(() => {
    const map = {};
    exercises.forEach((ex) => {
      if (ex?.id) map[ex.id] = ex;
    });
    return map;
  }, [exercises]);

  const getExerciseName = (id) => {
    if (!id) return "";
    const ex = exercisesMap[id];
    return ex?.name ?? String(id);
  };

  const findById = (id) => (id ? exercisesMap[id] ?? null : null);

  return { exercises, exercisesMap, loading, error, getExerciseName, findById };
}
